<template>
  <GamMainView :class="['walks-view', { 'is-mobile': isMobile }]">
    <div class="head">
      <div class="title-row">
        <span class="heading">{{ localize('walk.history.tab.my') }}</span>
      </div>
    </div>

    <div class="entries">
      <div class="top-stats-row">
        <div
          class="speed-entry-card"
          :class="{ 'speed-entry-card-static': walkStore.traceTotals.traceOthers > 0 }"
          @click.stop="openTracesInfo"
        >
          <div class="speed-entry-text">
            <span class="recap-square trace-other" />
            <span class="speed-entry-title">{{ localize('walks.traces.title') }}</span>
          </div>
          <span v-if="walkStore.traceTotals.traceOthers > 0" class="trace-total-value">{{
            walkStore.traceTotals.traceOthers
          }}</span>
          <span v-else class="speed-entry-arrow">›</span>
        </div>
      </div>

      <template v-if="isCardsLoading">
        <div class="months-section">
          <div class="months-strip">
            <div v-for="n in 4" :key="n" class="skeleton-month-tile" />
          </div>
        </div>
        <div class="daily-grid">
          <div v-for="n in 3" :key="n" class="skeleton-daily-card" />
        </div>
      </template>

      <template v-else-if="filteredCards.length || monthCards.length">
        <div v-if="monthCards.length" class="months-section">
          <div class="months-strip">
            <button
              v-for="card in monthCards"
              :key="card.month"
              type="button"
              class="month-tile"
              :class="{
                'month-tile-current': isCurrentMonth(card.month),
                'month-tile-active': selectedMonth === card.month,
              }"
              @click="toggleMonth(card.month)"
            >
              <span class="month-tile-label">{{ formatMonthLabel(card.month) }}</span>
              <span class="month-tile-metrics">
                <span class="month-tile-accuracy">{{ getDailyAccuracy(card) }}</span>
                <span class="month-tile-distance">{{ getMonthDistance(card) }}</span>
              </span>
            </button>
          </div>

          <Transition name="month-detail">
            <div v-if="selectedMonthCard" class="month-detail">
              <div class="daily-stats-compact">
                <span class="daily-stat">
                  <span class="daily-stat-num">{{ selectedMonthCard.reveals }}</span>
                  <span class="daily-stat-label">{{ localize('walks.months.sessions') }}</span>
                </span>
                <span class="daily-stat-sep">·</span>
                <span class="daily-stat">
                  <span class="daily-stat-num">{{ selectedMonthCard.spotted }}</span>
                  <span class="daily-stat-label">{{ localize('walks.months.puzzles') }}</span>
                </span>
              </div>
              <div class="daily-stats-compact">
                <span class="daily-stat">
                  <span class="daily-stat-num solved">{{ selectedMonthCard.solved }}</span>
                  <span class="recap-square solved" />
                </span>
                <span class="daily-stat-sep">·</span>
                <span class="daily-stat">
                  <span class="daily-stat-num wrong">{{ getWrongCount(selectedMonthCard) }}</span>
                  <span class="recap-square wrong" />
                </span>
                <template v-if="selectedMonthCard.notSubmitted">
                  <span class="daily-stat-sep">·</span>
                  <span class="daily-stat">
                    <span class="daily-stat-num not-submitted">{{ selectedMonthCard.notSubmitted }}</span>
                    <span class="recap-square not-submitted" />
                  </span>
                </template>
              </div>
            </div>
          </Transition>
        </div>

        <div v-if="filteredCards.length" class="daily-grid">
          <div v-for="card in filteredCards" :key="card.date" class="daily-card-wrapper">
            <div class="daily-card-carousel" @scroll.passive="(e: Event) => onCarouselScroll(card.date, e)">
              <div class="daily-card-slide daily-card-slide-main">
                <div class="daily-card-date">{{ formatCardDate(card.date) }}</div>
                <div class="daily-card-center-group">
                  <span class="daily-score-label">WALKED WITH ACCURACY</span>
                  <div class="daily-score-row">
                    <span class="daily-score-value">{{ getDailyDistance(card) }}</span>
                    <span class="daily-score-value">{{ getDailyAccuracy(card) }}</span>
                  </div>
                  <div class="daily-stats-compact">
                    <span class="daily-stat">
                      <span class="daily-stat-num">{{ card.reveals }}</span>
                      <span class="daily-stat-label">sessions</span>
                    </span>
                    <span class="daily-stat-sep">·</span>
                    <span class="daily-stat">
                      <span class="daily-stat-num">{{ card.spotted }}</span>
                      <span class="daily-stat-label">puzzles</span>
                    </span>
                  </div>
                  <div class="daily-stats-compact">
                    <span class="daily-stat">
                      <span class="daily-stat-num solved">{{ card.solved }}</span>
                      <span class="recap-square solved" />
                    </span>
                    <span class="daily-stat-sep">·</span>
                    <span class="daily-stat">
                      <span class="daily-stat-num wrong">{{
                        card.spotted - card.solved - (card.notSubmitted ?? 0)
                      }}</span>
                      <span class="recap-square wrong" />
                    </span>
                    <template v-if="card.notSubmitted">
                      <span class="daily-stat-sep">·</span>
                      <span class="daily-stat">
                        <span class="daily-stat-num not-submitted">{{ card.notSubmitted }}</span>
                        <span class="recap-square not-submitted" />
                      </span>
                    </template>
                  </div>
                </div>
              </div>

              <div
                v-for="segment in card.segments"
                :key="segment.sessionId"
                class="daily-card-slide daily-card-slide-segment"
                @click="openSegmentReveal(segment.sessionId, card.date)"
              >
                <div class="puzzle-grid">
                  <span v-for="(cell, ci) in getPuzzleGrid(segment)" :key="ci" class="puzzle-grid-cell" :class="cell" />
                </div>
                <div class="segment-footer">
                  <span class="segment-chip">{{ getSegmentFieldLabel(segment) }}</span>
                </div>
              </div>
            </div>

            <div v-if="card.segments?.length > 0" class="carousel-dots">
              <span
                v-for="dotIdx in 1 + card.segments.length"
                :key="dotIdx"
                class="carousel-dot"
                :class="{ active: (activeSlides[card.date] ?? 0) === dotIdx - 1 }"
              />
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="empty-walk-wrapper">
          <div class="empty-walk-cta" @click="goToWalk" @touchend.prevent="goToWalk">
            <span class="empty-walk-cta-text">{{ localize('walk.history.empty.cta') }}</span>
          </div>
        </div>
      </template>
    </div>

    <div class="nav-close-btn" @touchend.prevent="handleClose" @click="handleClose">
      <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
    </div>

    <WalkHistoryReveal
      :is-open="showHistoryReveal"
      :session-date="historyRevealDate"
      @close="showHistoryReveal = false"
      @deleted="showHistoryReveal = false"
    />

    <Transition name="drawer-slide">
      <div
        v-if="showTracesInfo"
        class="traces-info-backdrop"
        @click.self="showTracesInfo = false"
        @touchend.self.prevent="showTracesInfo = false"
      >
        <div ref="tracesDrawerRef" class="traces-info-card" @click.stop @touchend.stop>
          <div class="traces-info-head">
            <span class="traces-info-title">{{ localize('walks.traces.info.title') }}</span>
            <button type="button" class="traces-info-close" @click="showTracesInfo = false">✕</button>
          </div>
          <div class="traces-info-rows">
            <div class="traces-info-row">
              <span class="traces-info-mark">
                <span class="trace-toggle-mark"><span class="trace-toggle-square" /></span>
              </span>
              <p class="traces-info-body">{{ localize('walks.traces.info.enabled') }}</p>
            </div>
            <div class="traces-info-row">
              <span class="traces-info-mark">
                <span class="recap-square trace-mine" />
              </span>
              <p class="traces-info-body">{{ localize('walks.traces.info.own') }}</p>
            </div>
            <div class="traces-info-row">
              <span class="traces-info-mark">
                <span class="recap-square trace-other" />
              </span>
              <p class="traces-info-body">{{ localize('walks.traces.info.others') }}</p>
            </div>
            <div class="traces-info-row">
              <span class="traces-info-mark">
                <span class="recap-square trace-mine trace-fading" />
              </span>
              <p class="traces-info-body">{{ localize('walks.traces.info.fade') }}</p>
            </div>
          </div>
          <div class="traces-info-footer">
            <div class="traces-walk-cta" @click.stop="walkWithTraces" @touchend.stop.prevent="walkWithTraces">
              {{ localize('walks.traces.walk_cta') }}
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </GamMainView>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useWalkStore } from '@/stores/walk.store';
import GamIcon from '@/views/components/GamIcon.vue';
import GamMainView from '@/views/components/GamMainView.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { useGhostClickGuard } from '@/views/composables/hooks/useGhostClickGuard';
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ratingToBand } from '../utils/walk-rating-band';
import WalkHistoryReveal from './WalkHistoryReveal.vue';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const walkStore = useWalkStore();

const isCardsLoading = ref(false);
const showHistoryReveal = ref(false);
const historyRevealDate = ref('');
const showTracesInfo = ref(false);
const tracesDrawerRef = ref<HTMLElement | null>(null);
const { absorb: absorbGhostClick } = useGhostClickGuard();

useDrawerGesture(tracesDrawerRef, () => {
  showTracesInfo.value = false;
});

function openTracesInfo() {
  if (walkStore.traceTotals.traceOthers > 0) return;
  showTracesInfo.value = true;
}

function walkWithTraces() {
  walkStore.updateSettings({ mapEnabled: true });
  showTracesInfo.value = false;
  absorbGhostClick();
  router.push({ name: RouterNameType.WALK });
}

function goToWalk() {
  absorbGhostClick();
  router.push({ name: RouterNameType.WALK });
}

function handleClose() {
  absorbGhostClick();
  if (route.query.back === 'history' && window.history.length > 1) {
    router.back();
  } else if (authStore.userDetails?.username) {
    router.replace({ name: RouterNameType.USERNAME, params: { username: authStore.userDetails.username } });
  } else {
    router.replace({ name: RouterNameType.LOCATIONS });
  }
}

const filteredCards = computed(() => {
  const todayKey = new Date().toLocaleDateString('en-CA');
  return walkStore.dailyCards.filter((c) => {
    const hasAny = c.spotted > 0 || c.reveals > 0;
    if (c.date === todayKey && !hasAny) return false;
    return hasAny;
  });
});

const monthCards = computed(() => walkStore.monthCards.filter((c) => c.spotted > 0 || c.reveals > 0));

const selectedMonth = ref<string | null>(null);

const selectedMonthCard = computed(() => monthCards.value.find((c) => c.month === selectedMonth.value) ?? null);

const currentMonthKey = computed(() => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
});

function isCurrentMonth(month: string): boolean {
  return month === currentMonthKey.value;
}

function toggleMonth(month: string) {
  selectedMonth.value = selectedMonth.value === month ? null : month;
}

const activeSlides = reactive<Record<string, number>>({});

const GRID_SIZE = 50;

function getPuzzleGrid(segment: {
  total: number;
  solved: number;
  puzzleResults?: ('correct' | 'wrong' | 'corrected' | 'not_submitted')[];
}): string[] {
  const cells: string[] = [];
  if (segment.puzzleResults?.length) {
    for (let i = 0; i < GRID_SIZE; i++) {
      if (i < segment.puzzleResults.length) {
        const r = segment.puzzleResults[i];
        if (r === 'correct') cells.push('cell-correct');
        else if (r === 'corrected') cells.push('cell-corrected');
        else if (r === 'not_submitted') cells.push('cell-not-submitted');
        else cells.push('cell-wrong');
      } else {
        cells.push('cell-empty');
      }
    }
  } else {
    const wrong = segment.total - segment.solved;
    for (let i = 0; i < GRID_SIZE; i++) {
      if (i < segment.solved) cells.push('cell-correct');
      else if (i < segment.solved + wrong) cells.push('cell-wrong');
      else cells.push('cell-empty');
    }
  }
  return cells;
}

function getSegmentFieldLabel(segment: {
  breakEvenSpeedMps: number;
  puzzleRating: number;
  timeBankMs: number;
}): string {
  const speed = Math.round(segment.breakEvenSpeedMps * 3.6);
  const band = ratingToBand(segment.puzzleRating);
  const isVariant = segment.timeBankMs !== 1000;
  if (isVariant) {
    const sec = Math.round(segment.timeBankMs / 1000);
    return `${band.toUpperCase()}${speed} | ${sec}s`;
  }
  return `${band.toUpperCase()}${speed}`;
}

function getDailyAccuracy(card: { solved: number; spotted: number }): string {
  if (card.spotted === 0) return '–';
  const pct = Math.round((card.solved / card.spotted) * 100);
  return `${pct}%`;
}

function getDailyDistance(card: { totalDistanceM: number }): string {
  const meters = card.totalDistanceM ?? 0;
  if (meters === 0) return '–';
  const km = meters / 1000;
  return km < 1 ? `${Math.round(meters)}m` : `${km.toFixed(1)}km`;
}

function getMonthDistance(card: { totalDistanceM: number }): string {
  const km = (card.totalDistanceM ?? 0) / 1000;
  if (km >= 100) return `${Math.round(km)}km`;
  return getDailyDistance(card);
}

function getWrongCount(card: { spotted: number; solved: number; notSubmitted?: number }): number {
  return card.spotted - card.solved - (card.notSubmitted ?? 0);
}

function onCarouselScroll(date: string, e: Event) {
  const el = e.target as HTMLElement;
  const slideWidth = el.offsetWidth;
  if (slideWidth === 0) return;
  activeSlides[date] = Math.round(el.scrollLeft / slideWidth);
}

function formatCardDate(dateStr: string): string {
  const now = new Date();
  const todayKey = now.toLocaleDateString('en-CA');
  const yesterdayKey = new Date(now.getTime() - 86400000).toLocaleDateString('en-CA');

  if (dateStr === todayKey) return 'TODAY';
  if (dateStr === yesterdayKey) return 'YESTERDAY';

  const date = new Date(dateStr + 'T00:00:00');
  const weekday = date.toLocaleDateString('en', { weekday: 'short' }).slice(0, 2).toUpperCase();
  const day = date.getDate();
  const month = date.toLocaleDateString('en', { month: 'short' });
  return `${weekday} ${day} ${month}`;
}

function formatMonthLabel(monthStr: string): string {
  const [yearStr, monthNum] = monthStr.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthNum) - 1;
  if (!year || Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return monthStr.toUpperCase();
  }
  const date = new Date(Date.UTC(year, monthIndex, 1));
  const full = date.toLocaleDateString('en', { month: 'long', timeZone: 'UTC' }).toUpperCase();
  const currentYear = new Date().getFullYear();
  if (year === currentYear) return full;
  return `${full.slice(0, 3)} ${year}`;
}

async function openSegmentReveal(sessionId: string, date: string) {
  await walkStore.loadHistorySummary(sessionId);
  if (walkStore.historySummary) {
    historyRevealDate.value = date;
    showHistoryReveal.value = true;
    absorbGhostClick();
  }
}

async function fetchCards() {
  isCardsLoading.value = true;
  try {
    await Promise.all([
      walkStore.loadDailyCards(),
      walkStore.loadStreakRecords(),
      walkStore.loadFieldStats(),
      walkStore.loadTraceTotals(),
    ]);
  } finally {
    isCardsLoading.value = false;
  }
}

onMounted(() => {
  fetchCards();
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.main-view {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: stretch;
  background: var(--color-linear-gradient-vertical) var(--color-dark-700);
}

.nav-close-btn {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

.head {
  position: absolute;
  padding-bottom: var(--spacing-s);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  right: 0;
  background: transparent;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding-top: calc(var(--spacing-xl) + var(--spacing-xs));
  gap: var(--spacing-md);
  z-index: 2;
}

.walks-view:not(.is-mobile) .head {
  padding-bottom: calc(var(--spacing-xl) + var(--spacing-xs));
}

.walks-view.is-mobile .head {
  padding-top: calc(var(--spacing-l) * 2);
  gap: var(--spacing-l);
  padding-left: var(--spacing-l);
  padding-right: var(--spacing-l);
}

.title-row {
  max-width: 600px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--spacing-l);
}

.heading {
  font-size: 32px;
  font-weight: 700;
  line-height: 110%;
  color: var(--color-white-50);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.25em;
  box-shadow: var(--shadow-small);
}

.top-stats-row {
  max-width: 600px;
  width: 100%;
  display: flex;
  align-items: stretch;
  gap: 8px;
  position: relative;
  z-index: 1;
}

.score-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 14px 8px;
  border-radius: var(--radius-small, 8px);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  cursor: pointer;
  position: relative;
}

.score-card-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--color-white-30);
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.score-card-info-icon {
  color: var(--color-white-30);
}

.score-card-value {
  font-size: 22px;
  font-weight: 700;
  color: white;
}

.score-card-info-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 10px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--color-white-10);
  overflow: hidden;
}

.score-card-info-title {
  font-size: 10px;
  font-weight: 600;
  color: var(--color-white-50);
  text-align: center;
}

.score-card-info-sub {
  font-size: 9px;
  font-weight: 500;
  color: var(--color-white-40);
  text-align: center;
}

.score-card-label-arrow {
  font-size: 12px;
  color: var(--color-white-30);
}

.entries {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  padding: 114px 1.5rem 5rem;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.walks-view.is-mobile .entries {
  padding-top: 88px;
}

.daily-grid {
  max-width: 600px;
  width: 100%;
}

/* ── Month strip ───────────────────────────── */

.months-section {
  max-width: 600px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.months-strip {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.month-tile {
  flex: 0 0 auto;
  width: 104px;
  aspect-ratio: 1;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid var(--color-white-10);
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  opacity: 0.7;
  transition:
    transform 80ms ease,
    opacity 150ms ease,
    border-color 150ms ease,
    background 150ms ease;

  &:active {
    transform: scale(0.97);
  }

  &.month-tile-current {
    opacity: 1;
    background: var(--color-white-5);
    border-color: var(--color-white-20);
  }

  &.month-tile-active {
    opacity: 1;
    background: rgba(255, 255, 255, 0.08);
    border-color: var(--color-white-40);
  }
}

.month-tile-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-white-40);
  white-space: nowrap;
}

.month-tile-metrics {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.month-tile-accuracy {
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  color: var(--color-white);
  font-variant-numeric: tabular-nums;
}

.month-tile-distance {
  font-size: 17px;
  font-weight: 700;
  line-height: 1;
  color: var(--color-white-70);
  font-variant-numeric: tabular-nums;
}

.month-detail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid var(--color-white-10);
  background: var(--color-white-5);
}

.month-detail-enter-active,
.month-detail-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.month-detail-enter-from,
.month-detail-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── Speed records section ─────────────────── */

.speed-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  cursor: pointer;
}

.speed-entry-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  background: transparent;
  border: 1px solid var(--color-white-10);
  cursor: pointer;
  flex: 1;
  transition: transform 80ms ease;

  &:active {
    transform: scale(0.97);
  }

  &.speed-entry-card-static {
    cursor: default;

    &:active {
      transform: none;
    }
  }
}

.trace-total-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-white-70);
}

.traces-info-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2300;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.traces-info-card {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px 1.5rem 0;
  border-radius: 1.5rem 1.5rem 0 0;
  border: 1px solid var(--color-white-10);
  border-bottom: none;
  background: var(--color-dark-700);
  will-change: transform;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 200ms ease;

  .traces-info-card {
    transition: transform 200ms ease;
  }
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;

  .traces-info-card {
    transform: translateY(100%);
  }
}

.traces-info-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.traces-info-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-white);
}

.traces-info-close {
  border: none;
  background: transparent;
  color: var(--color-white-50);
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
}

.traces-info-rows {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.traces-info-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.traces-info-mark {
  flex: 0 0 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.traces-info-body {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
  color: var(--color-white-70);
}

.traces-info-footer {
  flex-shrink: 0;
  padding: 1.5rem 0 var(--safe-bottom-offset);
}

.traces-walk-cta {
  width: 100%;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 28px;
  border-radius: 2rem;
  border: 1px solid var(--color-pink-30);
  background: var(--color-pink-10);
  color: var(--color-white);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:active {
    transform: scale(0.95);
  }
}

.trace-fading {
  opacity: 0.4;
}

.trace-toggle-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
}

.trace-toggle-square {
  width: 6px;
  height: 6px;
  border-radius: 1.5px;
  background: oklch(0.7818 0.2418 355);
}

.speed-entry-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--color-white-70);
}

.speed-entry-count {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-white-40);
  margin-left: 4px;
}

.speed-entry-text {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.speed-entry-arrow {
  font-size: 18px;
  font-weight: 300;
  color: var(--color-white-30);
}

.speed-section-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-pink);
}

.empty-walk-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: -114px;
}

.empty-walk-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  max-width: 200px;
  width: 100%;
  border: none;
  border-radius: 0;
  background: var(--color-pink-10);
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms ease;

  @include hover {
    filter: brightness(1.2);
  }

  @include press;
}

.empty-walk-cta-text {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--color-white);
  text-align: center;
  padding: 1rem;
  line-height: 1.4;
}

/* ── Daily grid ────────────────────────────── */

.daily-grid {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  padding-top: var(--spacing-md);
}

.daily-card-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 12px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(
      160deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.06) 40%,
      rgba(255, 255, 255, 0.02) 60%,
      rgba(255, 255, 255, 0.12) 100%
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    pointer-events: none;
    z-index: 1;
  }
}

.daily-card-carousel {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
}

.daily-card-slide {
  position: relative;
  flex: 0 0 100%;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 14px;
  min-height: 190px;
  box-sizing: border-box;

  &.daily-card-slide-main {
    justify-content: center;

    .daily-card-date {
      position: absolute;
      top: 8px;
      left: 12px;
    }
  }

  &.daily-card-slide-segment {
    cursor: pointer;
    justify-content: center;
    gap: 8px;
    transition: transform 80ms ease;

    &:active {
      transform: scale(0.97);
    }
  }
}

.segment-footer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.segment-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 28px;
  height: 28px;
  padding: 0 4px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--color-white);
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid var(--color-white-20);
  border-radius: 2px;
}

.daily-card-date {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-white-40);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.daily-card-center-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1;
}

.daily-score-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-white-30);
}

.daily-score-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 1.5rem;
}

.daily-score-value {
  font-size: 36px;
  font-weight: 800;
  color: var(--color-white);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.daily-stats-compact {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.daily-stat {
  display: flex;
  align-items: center;
  gap: 4px;
}

.daily-stat-num {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-white-70);

  &.solved {
    color: var(--color-correct);
  }

  &.wrong {
    color: var(--color-incorrect);
  }

  &.not-submitted {
    color: var(--color-white-30);
  }
}

.daily-stat-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--color-white-40);
}

.daily-stat-sep {
  color: var(--color-white-20);
}

.recap-square {
  width: 12px;
  height: 12px;
  border-radius: 2px;

  &.solved {
    background: var(--color-correct);
  }

  &.wrong {
    background: var(--color-incorrect);
  }

  &.not-submitted {
    background: transparent;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  }

  &.trace-other {
    background: #f5c542;
  }

  &.trace-mine {
    background: oklch(0.7818 0.2418 355);
  }

  &.trace-clean {
    background: var(--color-correct);
  }
}

.puzzle-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;
  width: 100%;
  max-width: 180px;
  margin: 0 auto;
}

.puzzle-grid-cell {
  aspect-ratio: 1;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.06);

  &.cell-correct {
    background: var(--color-correct);
  }

  &.cell-wrong {
    background: var(--color-incorrect);
  }

  &.cell-corrected {
    background: var(--color-incorrect);
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 50%;
      height: 50%;
      border-radius: 50%;
      background: var(--color-correct);
      transform: translate(-50%, -50%);
    }
  }

  &.cell-not-submitted {
    background: transparent;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  }

  &.cell-empty {
    background: rgba(255, 255, 255, 0.06);
  }
}

.carousel-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding-bottom: 8px;
}

.carousel-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-white-15);
  transition:
    background 150ms ease,
    width 150ms ease,
    border-radius 150ms ease;

  &.active {
    width: 14px;
    border-radius: 3px;
    background: var(--color-white-50);
  }
}

.skeleton-daily-card {
  width: 100%;
  min-height: 190px;
  border-radius: 12px;
  background: var(--color-white-5);
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-month-tile {
  flex: 0 0 auto;
  width: 104px;
  aspect-ratio: 1;
  border-radius: 12px;
  background: var(--color-white-5);
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 0.4;
  }

  50% {
    opacity: 0.8;
  }
}
</style>
